import { Conversation, Message, Paginator, Participant } from '@twilio/conversations';
import { API_PATH } from 'constants/general';
import { LS_AUTH_DATA_KEY } from 'constants/user';
import { differenceInMinutes, format, isToday, isYesterday, Locale } from 'date-fns';
import { enGB } from 'date-fns/locale';
import { sortBy } from 'lodash';
import { Translate } from 'next-translate';
import { ConversationMessages } from 'state/contexts/ChatContext/ChatContext.types';
import { getChatContactTranslationStatusKey } from 'state/contexts/storageRequestContext/storageRequest.utils';
import { DayMessagesT, MessagesByDay, MessageT, SingleMessageT } from 'types/chat';
import { StorageRequest } from 'types/storageRequest';
import { getRequestConfig, handleResponse } from './apiService';
import { getLocalStorageData } from './utilsService';

type GetTwilioAccessToken = () => Promise<{ twilioAccessToken: string }>;
export const getTwilioAccessToken: GetTwilioAccessToken = () => {
  const localStorageAuthData = getLocalStorageData(LS_AUTH_DATA_KEY, null);

  const config = getRequestConfig({
    method: 'GET',
    token: localStorageAuthData?.token,
  });

  return fetch(`${API_PATH}/users/me/twilioAccessToken`, config).then(handleResponse);
};

type Participants = {
  client: Participant;
  provider: Participant;
};

export const getConersationParticipants = async (
  conversation: Conversation,
): Promise<Participants> => {
  const conversationParticipants = await conversation.getParticipants();

  return conversationParticipants.reduce((acc, participant) => {
    const { identity } = participant;

    return { ...acc, [identity as string]: participant };
  }, {} as Participants);
};

export const getDate = (date: Date, t: Translate, locale = enGB): string => {
  if (isToday(date)) {
    return t('today');
  }

  if (isYesterday(date)) {
    return t('yesterday');
  }

  return format(date, 'EEE, LLLL do', { locale });
};

export const mapMessage = (message: Message): MessageT => ({
  userId: message.author || '',
  text: message.body || '',
  insertedAt: message.dateCreated as Date,
  id: message.sid,
});

export type GroupMessagesByDay = (
  messages: Message[],
  t: Translate,
  locale?: Locale,
) => MessagesByDay;
export const groupMessagesByDay: GroupMessagesByDay = (messages, t, locale = enGB) => {
  const mappedMessages = messages.map((messsage) => mapMessage(messsage));

  return mappedMessages.reduce(
    (groupedMessages, newMessage) => {
      const day = getDate(newMessage.insertedAt, t, locale);

      if (day in groupedMessages) {
        groupedMessages[day].push(newMessage);
      } else {
        // eslint-disable-next-line no-param-reassign
        groupedMessages[day] = [newMessage];
      }

      return groupedMessages;
    },

    {} as MessagesByDay,
  );
};

export const addLastMessage = (
  message: Message,
  messagesByDay: MessagesByDay | null,
  t: Translate,
  locale = enGB,
): MessagesByDay => {
  const newMessage = mapMessage(message);
  const day = getDate(newMessage.insertedAt, t, locale);

  if (!messagesByDay) return { [day]: [newMessage] };
  const lastKey = Object.keys(messagesByDay)[Object.keys(messagesByDay).length - 1];

  const obj = { ...messagesByDay, [lastKey]: [...messagesByDay[lastKey], newMessage] };

  return obj;
};

export type GroupChatMessagesT = (
  messages: Message[],
  t: Translate,
  locale?: Locale,
) => DayMessagesT[];
export const groupChatMessages: GroupChatMessagesT = (messages, t, locale = enGB) => {
  const mappedMessages = messages.map((messsage) => mapMessage(messsage));
  const sorted = sortBy(mappedMessages, (msg) => msg.insertedAt);

  return sorted.reduce<DayMessagesT[]>((groupedMessages, newMessage) => {
    const day = getDate(newMessage.insertedAt, t, locale);

    if (groupedMessages.length > 0) {
      const lastDay = groupedMessages[groupedMessages.length - 1];
      if (lastDay.day === day) {
        const { messages } = lastDay;
        const lastMessagesItem = messages[messages.length - 1];
        if (lastMessagesItem.isGroup) {
          const { userId, startTime } = lastMessagesItem;
          if (
            userId === newMessage.userId &&
            differenceInMinutes(newMessage.insertedAt, startTime) <= 5
          ) {
            lastMessagesItem.messages = [...lastMessagesItem.messages, newMessage];
          } else {
            lastDay.messages = [
              ...messages,
              { isGroup: false, message: newMessage } as SingleMessageT,
            ];
          }
        } else {
          const { message: lastMessage } = lastMessagesItem;
          const lastMessageDate = lastMessage.insertedAt;
          if (
            lastMessage.userId === newMessage.userId &&
            differenceInMinutes(newMessage.insertedAt, lastMessageDate) <= 5
          ) {
            lastDay.messages[messages.length - 1] = {
              isGroup: true,
              userId: lastMessage.userId,
              startTime: lastMessageDate,
              messages: [lastMessage, newMessage],
            };
          } else {
            lastDay.messages = [...messages, { isGroup: false, message: newMessage }];
          }
        }

        return groupedMessages;
      }
      const newDay: DayMessagesT = {
        day,
        messages: [{ isGroup: false, message: newMessage }],
      };

      return [...groupedMessages, newDay];
    }
    const newDay: DayMessagesT = {
      day,
      messages: [{ isGroup: false, message: newMessage }],
    };

    return [newDay];
  }, [] as DayMessagesT[]);
};

export const findConversation = (
  conversations: Conversation[],
  conversationSid: string,
): Conversation | undefined => {
  return conversations.find((c) => c.sid === conversationSid);
};

export const getLastmessage = (
  conversationMessages: ConversationMessages | null,
  t: Translate,
  storageRequest: StorageRequest,
  userId?: string,
): string => {
  if (!conversationMessages || !storageRequest.conversationSid)
    return t(
      getChatContactTranslationStatusKey(
        storageRequest.requestStatus,
        storageRequest.type,
      ),
    );

  const messages = conversationMessages[storageRequest.conversationSid]?.messages || [];
  if (!messages || messages.length === 0) {
    return t(
      getChatContactTranslationStatusKey(
        storageRequest.requestStatus,
        storageRequest.type,
      ),
    );
  }
  const lastMessage = messages[messages.length - 1];

  const name =
    lastMessage.author === userId
      ? 'You'
      : storageRequest.type === 'received'
      ? storageRequest.clientName
      : storageRequest.storageProvider.name;

  return `${name}: ${lastMessage.body}`;
};

export const getPreviousMessages = async (
  messagesPaginator: Paginator<Message>,
): Promise<Paginator<Message> | null> => {
  if (messagesPaginator.hasPrevPage) {
    const paginator = await messagesPaginator.prevPage().then((messages) => messages);

    return paginator;
  }

  return null;
};
